import './coapplicantIncomeDetailsPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Disclosure } from '../../../components/Disclosure/Disclosure';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const {
    coapplicantAdditionalIncomeAmount,
    coapplicantAdditionalIncomeDurationMonths,
    coapplicantAdditionalIncomeDurationYears,
    coapplicantAdditionalIncomeEmployerName,
    coapplicantAdditionalIncomeFrequency,
    coapplicantAdditionalIncomeJobTitle,
    coapplicantAdditionalIncomeType,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantAdditionalIncomeAmount,
    setCoapplicantAdditionalIncomeDurationMonths,
    setCoapplicantAdditionalIncomeDurationYears,
    setCoapplicantAdditionalIncomeEmployerName,
    setCoapplicantAdditionalIncomeFrequency,
    setCoapplicantAdditionalIncomeJobTitle,
    setCoapplicantAdditionalIncomeType,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      incomeAmountValue: toCurrencyString(coapplicantAdditionalIncomeAmount),
      incomeDurationMonthsValue: coapplicantAdditionalIncomeDurationMonths,
      incomeDurationYearsValue: coapplicantAdditionalIncomeDurationYears,
      incomeEmployerNameValue: coapplicantAdditionalIncomeEmployerName,
      incomeFrequencyValue: coapplicantAdditionalIncomeFrequency,
      incomeJobTitleValue: coapplicantAdditionalIncomeJobTitle,
      incomeTypeValue: coapplicantAdditionalIncomeType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const incomeTypeValidation = {
    required: t('validation.incomeType.required'),
  };

  const incomeFrequencyValidation = {
    required: t('validation.incomeFrequency.required'),
  };

  const incomeAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.incomeAmount.required'),
    min: {
      value: 0,
      message: t('validation.incomeAmount.min', { min: '$0' }),
    },
  };

  const incomeEmployerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const incomeJobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const incomeDurationYearsValidation = {
    required: t('validation.durationYears.required'),
  };

  const incomeDurationMonthsValidation = {
    required: t('validation.durationMonths.required'),
  };

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue') === IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      incomeTypeValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeAmountValue,
    } = values;
    if (!incomeTypeValue || !incomeFrequencyValue || !incomeAmountValue) {
      return;
    }

    setCoapplicantAdditionalIncomeAmount(incomeAmountValue);
    setCoapplicantAdditionalIncomeFrequency(incomeFrequencyValue);
    setCoapplicantAdditionalIncomeType(incomeTypeValue);

    if (showSecondaryEmploymentFields) {
      setCoapplicantAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
      setCoapplicantAdditionalIncomeDurationYears(incomeDurationYearsValue);
      setCoapplicantAdditionalIncomeEmployerName(incomeEmployerNameValue);
      setCoapplicantAdditionalIncomeJobTitle(incomeJobTitleValue);
    } else {
      setCoapplicantAdditionalIncomeDurationMonths(null);
      setCoapplicantAdditionalIncomeDurationYears(null);
      setCoapplicantAdditionalIncomeEmployerName(null);
      setCoapplicantAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-income-details-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantIncomeDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantIncomeDetails.form.h1}>
                {t('CoapplicantIncomeDetailsPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantIncomeDetails.form.h2}>
                {t('CoapplicantIncomeDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantIncomeDetails.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register('incomeTypeValue', incomeTypeValidation)}
                    error={errors.incomeTypeValue}
                    errorTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .incomeTypeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .incomeTypeSelect
                    }
                    label={t('CoapplicantIncomeDetailsPage.incomeType')}
                    options={incomeTypeOptions}
                  />
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      data-testid={
                        locator.pages.coapplicantIncomeDetails.form
                          .employerNameInput
                      }
                      error={errors.incomeEmployerNameValue}
                      errorTestId={
                        locator.pages.coapplicantIncomeDetails.form
                          .employerNameInputError
                      }
                      label={t('CoapplicantIncomeDetailsPage.employerName')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      data-testid={
                        locator.pages.coapplicantIncomeDetails.form
                          .jobTitleInput
                      }
                      error={errors.incomeJobTitleValue}
                      errorTestId={
                        locator.pages.coapplicantIncomeDetails.form
                          .jobTitleInputError
                      }
                      label={t('CoapplicantIncomeDetailsPage.jobTitle')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <Select
                        {...register(
                          'incomeDurationYearsValue',
                          incomeDurationYearsValidation,
                        )}
                        error={errors.incomeDurationYearsValue}
                        errorTestId={
                          locator.pages.coapplicantIncomeDetails.form
                            .yearSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantIncomeDetails.form.yearSelect
                        }
                        label={t('CoapplicantIncomeDetailsPage.durationYears')}
                        options={Array.from({ length: 11 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>

                    <div className="input-half">
                      <Select
                        {...register(
                          'incomeDurationMonthsValue',
                          incomeDurationMonthsValidation,
                        )}
                        error={errors.incomeDurationMonthsValue}
                        errorTestId={
                          locator.pages.coapplicantIncomeDetails.form
                            .monthSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantIncomeDetails.form
                            .monthSelect
                        }
                        label={t('CoapplicantIncomeDetailsPage.durationMonths')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register(
                      'incomeFrequencyValue',
                      incomeFrequencyValidation,
                    )}
                    error={errors.incomeFrequencyValue}
                    errorTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .frequencySelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .frequencySelect
                    }
                    label={t('CoapplicantIncomeDetailsPage.incomeFrequency')}
                    options={incomeFrequencyOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    data-testid={
                      locator.pages.coapplicantIncomeDetails.form.amountInput
                    }
                    error={errors.incomeAmountValue}
                    errorTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .amountInputError
                    }
                    label={t('CoapplicantIncomeDetailsPage.incomeAmount')}
                    currency
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantIncomeDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantIncomeDetailsPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={locator.pages.coapplicantIncomeDetails.form.alimonyDisclosure}
              >
                <Disclosure i18nKey="disclosures.alimony" />
              </p>
            </div>
            <footer
              data-testid={locator.pages.coapplicantIncomeDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantIncomeDetailsPage';
